import {
  Authentication,
  prepareActiveUserOptions,
  renderDataGridLocaleText,
  renderDatePickerLocaleText,
  renderAutoCompleteLocaleText,
} from 'utils';
import {
  Box,
  Tab,
  Tabs,
  Paper,
  Alert,
  useTheme,
  TextField,
  Typography,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
} from '@mui/material';
import {
  Tab3BookSummaryRow,
  buildTab3ChartData,
  Tab3ActivityTypeRow,
  calculateTab3GrandTotal,
  buildTab3BookSummaryRows,
} from './tab3-summary';
import {
  ActivitySummaryRow,
  ActivitySummaryBreakdown,
  ActivitySummaryDetailRow,
  buildActivitySummaryRows,
  ACTIVITY_SUMMARY_IDLE_ROW_ID,
  calculateActivitySummaryGrandTotal,
} from './activity-summary';
import dayjs, { Dayjs } from 'dayjs';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import { DatePicker } from '@mui/x-date-pickers';
import { BarChart } from '@mui/x-charts/BarChart';
import { useHolidayCache } from './use-holiday-cache';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useDataGridLocaleText, useLookup } from 'hooks';
import { getRequiredHolidayYears } from './holiday-years';
import { prepareActivityMonitoringBooks } from './book-options';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import ActivityBreakdownSwitcher from './activity-breakdown-switcher';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { usePermissionActivityCache } from './use-permission-activity-cache';
import { CustomToolbar, ZebraDataGrid, CustomNoRowsOverlay } from 'components';
import { ActivityBoardItem, ActivityType, ShortBook, User } from 'library/types';
import { calculateEffectivePermissions, calculateExpectedWorkingHours } from './working-time';
import { useCallback, useEffect, useMemo, useRef, useState, SyntheticEvent, ReactNode } from 'react';
import { UserActivityRow, UserSummaryRow, buildUserSummaryRows, calculateAdjustedExpectedHours } from './user-summary';
import { ACTIVITY_BOARD_PERMISSION_CODES, canReadActivityBoardPermissions } from 'constants/activity-board-permissions';

const EMPTY_ACTIVITY_BOARD_ITEMS: readonly ActivityBoardItem[] = [];

const loadActiveUsers = async () => {
  const response = await apiService.getUsers();

  return { ...response, data: response.isSuccess ? prepareActiveUserOptions(response.data) : [] };
};

const loadMonitoringBooks = async () => {
  const response = await apiService.getCompanies();

  return { ...response, data: response.isSuccess ? prepareActivityMonitoringBooks(response.data) : [] };
};

// Tab Panel component
type TabPanelProps = {
  children?: ReactNode;
  index: number;
  value: number;
};

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      id={`activity-monitoring-tabpanel-${index}`}
      aria-labelledby={`activity-monitoring-tab-${index}`}
      hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const ActivityMonitoring = () => {
  const intl = useIntl();
  const theme = useTheme();

  const dataGridLocaleText = useDataGridLocaleText();
  const { items: users } = useLookup<User>(loadActiveUsers);
  const { items: books } = useLookup<ShortBook>(loadMonitoringBooks);
  const { items: activityTypes } = useLookup<ActivityType>(apiService.getActivityTypes);

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const arePermissionsInitialized = permissionCodes[0] !== STORAGE_KEY_ENUM.NON_INITIALIZED;
  const canListActivityBoardItemsAsUser = permissionCodes.includes('list-activity-board-item');
  const canListActivityBoardItemsAsAdmin = permissionCodes.includes('list-activity-board-item-admin');
  const isActivityBoardItemUserScope = canListActivityBoardItemsAsUser && !canListActivityBoardItemsAsAdmin;
  const hasPermissionActivityAdminScope = permissionCodes.includes(ACTIVITY_BOARD_PERMISSION_CODES.admin);
  const canReadPermissionActivities = canReadActivityBoardPermissions(permissionCodes);
  const isPermissionActivityScopeCompatible = hasPermissionActivityAdminScope || isActivityBoardItemUserScope;
  const permissionActivityScope =
    arePermissionsInitialized && canReadPermissionActivities
      ? hasPermissionActivityAdminScope
        ? 'admin'
        : 'user'
      : null;
  const { items: permissionActivities, status: permissionActivityStatus } =
    usePermissionActivityCache(permissionActivityScope);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Data states
  const [loading, setLoading] = useState(false);
  const [activityBoardItems, setActivityBoardItems] = useState<ActivityBoardItem[]>([]);
  const [activityDataSignature, setActivityDataSignature] = useState<string | null>(null);
  const [activityLoadError, setActivityLoadError] = useState(false);

  // Tab 1 Filter states
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));

  // Tab 2 Filter states
  const [tab2Loading, setTab2Loading] = useState(false);
  const [tab2EndDate, setTab2EndDate] = useState<Dayjs | null>(dayjs());
  const [tab2SelectedUser, setTab2SelectedUser] = useState<User | null>(null);
  const [tab2StartDate, setTab2StartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [tab2ActivityBoardItems, setTab2ActivityBoardItems] = useState<ActivityBoardItem[]>([]);
  const [tab2ActivityDataSignature, setTab2ActivityDataSignature] = useState<string | null>(null);
  const [tab2ActivityLoadError, setTab2ActivityLoadError] = useState(false);
  const [activitySummaryBreakdown, setActivitySummaryBreakdown] = useState<ActivitySummaryBreakdown>('user');

  // Tab 3 Filter states
  const [tab3Loading, setTab3Loading] = useState(false);
  const [tab3EndDate, setTab3EndDate] = useState<Dayjs | null>(dayjs());
  const [tab3SelectedBook, setTab3SelectedBook] = useState<ShortBook | null>(null);
  const [tab3SelectedUser, setTab3SelectedUser] = useState<User | null>(null);
  const [tab3StartDate, setTab3StartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [tab3ActivityBoardItems, setTab3ActivityBoardItems] = useState<ActivityBoardItem[]>([]);
  const [tab3ActivityLoadError, setTab3ActivityLoadError] = useState(false);
  const tab1ActivityRequestIdRef = useRef(0);
  const tab2ActivityRequestIdRef = useRef(0);
  const tab3ActivityRequestIdRef = useRef(0);

  const requiredHolidayYears = useMemo(
    () =>
      getRequiredHolidayYears(permissionActivities, [
        { startDate, endDate },
        { startDate: tab2StartDate, endDate: tab2EndDate },
      ]),
    [permissionActivities, startDate, endDate, tab2StartDate, tab2EndDate]
  );
  const { holidays, status: holidayStatus } = useHolidayCache(requiredHolidayYears);
  const isPermissionActivityDataReady =
    canReadPermissionActivities && permissionActivityStatus === 'ready' && isPermissionActivityScopeCompatible;
  const isPermissionActivityDataLoading =
    canReadPermissionActivities && (permissionActivityStatus === 'idle' || permissionActivityStatus === 'loading');
  const isSupportingDataLoading = isPermissionActivityDataLoading || holidayStatus === 'loading';

  const loggedUserName = Authentication.getLoggedUser();
  const currentUser = useMemo(
    () => users.find((user) => user.nameSurname === loggedUserName) ?? null,
    [users, loggedUserName]
  );
  const tab1SelectedUser = isActivityBoardItemUserScope ? currentUser : selectedUser;
  const tab1SelectedUserId = tab1SelectedUser?.id ?? null;
  const tab3SelectedUserId = isActivityBoardItemUserScope ? (currentUser?.id ?? null) : (tab3SelectedUser?.id ?? null);
  const tab1ActivityQuerySignature = [
    isActivityBoardItemUserScope ? 'user' : 'admin',
    tab1SelectedUserId ?? 'all',
    startDate?.startOf('day').valueOf() ?? 'no-start',
    endDate?.endOf('day').valueOf() ?? 'no-end',
  ].join(':');
  const tab2ActivityQuerySignature = [
    tab2SelectedUser?.id ?? 'all',
    tab2StartDate?.startOf('day').valueOf() ?? 'no-start',
    tab2EndDate?.endOf('day').valueOf() ?? 'no-end',
  ].join(':');

  // Tab change handler
  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    if (isActivityBoardItemUserScope && newValue !== 0 && newValue !== 2) return;

    setTabValue(newValue);
  };

  // Fetch activity board items
  const getActivityBoardItems = useCallback(async () => {
    const requestId = ++tab1ActivityRequestIdRef.current;

    if (!arePermissionsInitialized || (isActivityBoardItemUserScope && !tab1SelectedUserId)) {
      setActivityBoardItems([]);
      setActivityDataSignature(null);
      setActivityLoadError(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setActivityLoadError(false);

      const response = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: tab1SelectedUserId,
        startDate: startDate ? startDate.startOf('day').toISOString() : null,
        endDate: endDate ? endDate.endOf('day').toISOString() : null,
      });

      if (requestId === tab1ActivityRequestIdRef.current) {
        setActivityBoardItems(response.isSuccess ? response.data : []);
        setActivityDataSignature(response.isSuccess ? tab1ActivityQuerySignature : null);
        setActivityLoadError(!response.isSuccess);
      }
    } catch {
      if (requestId === tab1ActivityRequestIdRef.current) {
        setActivityBoardItems([]);
        setActivityDataSignature(null);
        setActivityLoadError(true);
      }
    } finally {
      if (requestId === tab1ActivityRequestIdRef.current) setLoading(false);
    }
  }, [
    arePermissionsInitialized,
    isActivityBoardItemUserScope,
    tab1SelectedUserId,
    startDate,
    endDate,
    tab1ActivityQuerySignature,
  ]);

  // Users filtered by isActivityUser flag
  const activityUsers = useMemo(() => users.filter((u) => u.isActivityUser), [users]);

  // Permission types from both metadata and the canonical permission response.
  const permissionActivityTypeIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...activityTypes
            .filter((activityType) => activityType.isPermissionActivity)
            .map((activityType) => activityType.id),
          ...permissionActivities.map((permission) => permission.activityTypeId),
        ])
      ),
    [activityTypes, permissionActivities]
  );

  const hasValidTab1Range = Boolean(startDate?.isValid() && endDate?.isValid() && !endDate.isBefore(startDate, 'day'));
  const hasValidTab2Range = Boolean(
    tab2StartDate?.isValid() && tab2EndDate?.isValid() && !tab2EndDate.isBefore(tab2StartDate, 'day')
  );
  const isTab1IdleAvailable =
    isPermissionActivityDataReady &&
    holidayStatus === 'ready' &&
    hasValidTab1Range &&
    !loading &&
    !activityLoadError &&
    activityDataSignature === tab1ActivityQuerySignature;
  const isTab2IdleAvailable =
    isPermissionActivityDataReady &&
    holidayStatus === 'ready' &&
    hasValidTab2Range &&
    !tab2Loading &&
    !tab2ActivityLoadError &&
    tab2ActivityDataSignature === tab2ActivityQuerySignature;
  const tab1ReportActivityItems =
    !loading && activityDataSignature === tab1ActivityQuerySignature ? activityBoardItems : EMPTY_ACTIVITY_BOARD_ITEMS;
  const tab2ReportActivityItems =
    !tab2Loading && tab2ActivityDataSignature === tab2ActivityQuerySignature
      ? tab2ActivityBoardItems
      : EMPTY_ACTIVITY_BOARD_ITEMS;

  const expectedHours = useMemo(
    () => (startDate && endDate ? calculateExpectedWorkingHours(startDate, endDate, holidays) : 0),
    [startDate, endDate, holidays]
  );

  const tab2ExpectedHours = useMemo(
    () => (tab2StartDate && tab2EndDate ? calculateExpectedWorkingHours(tab2StartDate, tab2EndDate, holidays) : 0),
    [tab2StartDate, tab2EndDate, holidays]
  );

  const tab1EffectivePermissions = useMemo(
    () =>
      isTab1IdleAvailable && startDate && endDate
        ? calculateEffectivePermissions(permissionActivities, startDate, endDate, holidays, tab1SelectedUserId)
        : [],
    [isTab1IdleAvailable, permissionActivities, startDate, endDate, holidays, tab1SelectedUserId]
  );

  const tab2EffectivePermissions = useMemo(
    () =>
      isTab2IdleAvailable && tab2StartDate && tab2EndDate
        ? calculateEffectivePermissions(
            permissionActivities,
            tab2StartDate,
            tab2EndDate,
            holidays,
            tab2SelectedUser?.id ?? null
          )
        : [],
    [isTab2IdleAvailable, permissionActivities, tab2StartDate, tab2EndDate, holidays, tab2SelectedUser]
  );

  const tab1ScopedUsers = useMemo(() => {
    if (isActivityBoardItemUserScope) return currentUser ? [currentUser] : [];
    if (selectedUser) return activityUsers.filter((user) => user.id === selectedUser.id);
    return activityUsers;
  }, [activityUsers, currentUser, isActivityBoardItemUserScope, selectedUser]);

  const userSummaryRows = useMemo(
    () =>
      buildUserSummaryRows({
        activityBoardItems: tab1ReportActivityItems,
        effectivePermissions: tab1EffectivePermissions,
        additionalPermissionActivityTypeIds: permissionActivityTypeIds,
        activityTypes,
        scopedUsers: tab1ScopedUsers,
        expectedHours,
        idleAvailable: isTab1IdleAvailable,
      }),
    [
      tab1ReportActivityItems,
      tab1EffectivePermissions,
      permissionActivityTypeIds,
      activityTypes,
      tab1ScopedUsers,
      expectedHours,
      isTab1IdleAvailable,
    ]
  );

  // Total spent hours
  const totalSpentHours = useMemo(
    () => parseFloat(userSummaryRows.reduce((sum, row) => sum + row.spentHours, 0).toFixed(2)),
    [userSummaryRows]
  );

  const displayedExpectedHours = useMemo(
    () =>
      calculateAdjustedExpectedHours({
        effectivePermissions: tab1EffectivePermissions,
        expectedHours,
        idleAvailable: isTab1IdleAvailable,
        selectedUserId: tab1SelectedUserId,
      }),
    [tab1EffectivePermissions, expectedHours, isTab1IdleAvailable, tab1SelectedUserId]
  );

  // DataGrid columns
  const columns: GridColDef<UserSummaryRow>[] = useMemo(
    () => [
      {
        field: 'nameSurname',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.nameSurname',
          defaultMessage: 'Kullanıcı Adı',
        }),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'idleHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.idleHours',
          defaultMessage: 'Atıl Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
        valueFormatter: (value) => (value == null ? '—' : value),
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 90,
      },
    ],
    [intl]
  );

  // Tab 1: DataGrid columns for detail (activity type) rows
  const tab1DetailColumns: GridColDef<UserActivityRow>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.activityType',
          defaultMessage: 'Aktivite Türü',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 1: Detail panel content (activity type breakdown per user)
  const getTab1DetailPanelContent = useCallback(
    (params: GridRowParams<UserSummaryRow>) => {
      const row = params.row;
      if (!row.activityRows || row.activityRows.length === 0) return null;

      return (
        <Box sx={{ p: 2 }}>
          <ZebraDataGrid<UserActivityRow>
            columns={tab1DetailColumns}
            rows={row.activityRows}
            getRowId={(r) => r.activityTypeId}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [tab1DetailColumns, dataGridLocaleText]
  );

  // Tab 1: Detail panel height
  const getTab1DetailPanelHeight = useCallback((params: GridRowParams<UserSummaryRow>) => {
    const count = params.row.activityRows?.length || 0;
    if (count === 0) return 0;
    return count * 36 + 56 + 16;
  }, []);

  // Chart data (only users with spentHours > 0)
  const chartData = useMemo(() => {
    const filtered = userSummaryRows.filter((row) => row.spentHours > 0);
    return {
      yLabels: filtered.map((row) => row.nameSurname),
      values: filtered.map((row) => row.spentHours),
    };
  }, [userSummaryRows]);

  // Tab 2: Fetch activity board items
  const getTab2ActivityBoardItems = useCallback(async () => {
    const requestId = ++tab2ActivityRequestIdRef.current;

    if (!arePermissionsInitialized || isActivityBoardItemUserScope) {
      setTab2ActivityBoardItems([]);
      setTab2ActivityDataSignature(null);
      setTab2ActivityLoadError(false);
      setTab2Loading(false);
      return;
    }

    try {
      setTab2Loading(true);
      setTab2ActivityLoadError(false);

      const response = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: tab2SelectedUser?.id || null,
        startDate: tab2StartDate ? tab2StartDate.startOf('day').toISOString() : null,
        endDate: tab2EndDate ? tab2EndDate.endOf('day').toISOString() : null,
      });

      if (requestId === tab2ActivityRequestIdRef.current) {
        setTab2ActivityBoardItems(response.isSuccess ? response.data : []);
        setTab2ActivityDataSignature(response.isSuccess ? tab2ActivityQuerySignature : null);
        setTab2ActivityLoadError(!response.isSuccess);
      }
    } catch {
      if (requestId === tab2ActivityRequestIdRef.current) {
        setTab2ActivityBoardItems([]);
        setTab2ActivityDataSignature(null);
        setTab2ActivityLoadError(true);
      }
    } finally {
      if (requestId === tab2ActivityRequestIdRef.current) setTab2Loading(false);
    }
  }, [
    arePermissionsInitialized,
    isActivityBoardItemUserScope,
    tab2SelectedUser,
    tab2StartDate,
    tab2EndDate,
    tab2ActivityQuerySignature,
  ]);

  // Tab 2: Compute the visible master rows and their selected detail breakdown.
  const activityTypeSummaryRows = useMemo(
    () =>
      buildActivitySummaryRows({
        activityBoardItems: tab2ReportActivityItems,
        activityTypes,
        activityUsers,
        books,
        breakdown: activitySummaryBreakdown,
        selectedUserId: tab2SelectedUser?.id,
        effectivePermissions: tab2EffectivePermissions,
        unknownProjectLabel: intl.formatMessage({
          id: 'activityMonitoring.breakdown.unknownProject',
          defaultMessage: 'Tanımsız Proje',
        }),
        idle: isTab2IdleAvailable
          ? {
              expectedHours: tab2ExpectedHours,
              label: intl.formatMessage({
                id: 'activityMonitoring.idleHoursLabel',
                defaultMessage: 'Atıl Süre',
              }),
              permissionActivityTypeIds,
            }
          : undefined,
      }),
    [
      tab2ReportActivityItems,
      activityTypes,
      activityUsers,
      books,
      activitySummaryBreakdown,
      tab2SelectedUser,
      tab2EffectivePermissions,
      tab2ExpectedHours,
      isTab2IdleAvailable,
      intl,
      permissionActivityTypeIds,
    ]
  );

  // Tab 2: Grand total always reconciles with the visible master rows.
  const tab2GrandTotal = useMemo(
    () => calculateActivitySummaryGrandTotal(activityTypeSummaryRows),
    [activityTypeSummaryRows]
  );

  // Tab 2: Chart data (only activity types with totalHours > 0, excluding Atıl Süre)
  const tab2ChartData = useMemo(() => {
    const filtered = activityTypeSummaryRows.filter(
      (row) => row.activityTypeId !== ACTIVITY_SUMMARY_IDLE_ROW_ID && row.totalHours > 0
    );
    return {
      yLabels: filtered.map((row) => row.activityTypeTitle),
      values: filtered.map((row) => row.totalHours),
    };
  }, [activityTypeSummaryRows]);

  // Tab 2: DataGrid columns for master rows
  const tab2Columns: GridColDef<ActivitySummaryRow>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.activityType',
          defaultMessage: 'Aktivite Türü',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'regularHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.regularHours',
          defaultMessage: 'Mesai İçi (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 2: DataGrid columns for the selected detail breakdown.
  const tab2DetailColumns: GridColDef<ActivitySummaryDetailRow>[] = useMemo(
    () => [
      {
        field: 'label',
        headerName: intl.formatMessage(
          activitySummaryBreakdown === 'user'
            ? {
                id: 'activityMonitoring.nameSurname',
                defaultMessage: 'Kullanıcı Adı',
              }
            : {
                id: 'activityMonitoring.projectName',
                defaultMessage: 'Proje Adı',
              }
        ),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'regularHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.regularHours',
          defaultMessage: 'Mesai İçi (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [activitySummaryBreakdown, intl]
  );

  // Tab 2: Detail panel content (user or project breakdown per activity type)
  const getTab2DetailPanelContent = useCallback(
    (params: GridRowParams<ActivitySummaryRow>) => {
      const row = params.row;
      if (row.detailRows.length === 0) return null;

      return (
        <Box sx={{ p: 2 }}>
          <ZebraDataGrid<ActivitySummaryDetailRow>
            columns={tab2DetailColumns}
            rows={row.detailRows}
            getRowId={(detailRow) => detailRow.id}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [tab2DetailColumns, dataGridLocaleText]
  );

  // Tab 2: Detail panel height
  const getTab2DetailPanelHeight = useCallback((params: GridRowParams<ActivitySummaryRow>) => {
    const detailCount = params.row.detailRows.length;
    if (detailCount === 0) return 0;
    return detailCount * 36 + 56 + 16;
  }, []);

  // Tab 2: Pinned rows (grand total at bottom)
  const tab2PinnedRows = useMemo(
    () => ({
      bottom: [
        {
          activityTypeId: -1,
          activityTypeTitle: intl.formatMessage({
            id: 'activityMonitoring.grandTotal',
            defaultMessage: 'Genel Toplam',
          }),
          regularHours: tab2GrandTotal.regularHours,
          overtimeHours: tab2GrandTotal.overtimeHours,
          totalHours: tab2GrandTotal.totalHours,
          detailRows: [],
        },
      ],
    }),
    [intl, tab2GrandTotal]
  );

  // Tab 3: Fetch activity board items
  const getTab3ActivityBoardItems = useCallback(async () => {
    const requestId = ++tab3ActivityRequestIdRef.current;

    if (!arePermissionsInitialized || (isActivityBoardItemUserScope && !tab3SelectedUserId)) {
      setTab3ActivityBoardItems([]);
      setTab3ActivityLoadError(false);
      setTab3Loading(false);
      return;
    }

    try {
      setTab3Loading(true);
      setTab3ActivityLoadError(false);

      const response = await apiService.getActivityBoardItems({
        bookId: tab3SelectedBook?.id || null,
        projectId: null,
        userId: tab3SelectedUserId,
        startDate: tab3StartDate ? tab3StartDate.startOf('day').toISOString() : null,
        endDate: tab3EndDate ? tab3EndDate.endOf('day').toISOString() : null,
      });

      if (requestId === tab3ActivityRequestIdRef.current) {
        setTab3ActivityBoardItems(response.isSuccess ? response.data : []);
        setTab3ActivityLoadError(!response.isSuccess);
      }
    } catch {
      if (requestId === tab3ActivityRequestIdRef.current) {
        setTab3ActivityBoardItems([]);
        setTab3ActivityLoadError(true);
      }
    } finally {
      if (requestId === tab3ActivityRequestIdRef.current) setTab3Loading(false);
    }
  }, [
    arePermissionsInitialized,
    isActivityBoardItemUserScope,
    tab3SelectedBook,
    tab3SelectedUserId,
    tab3StartDate,
    tab3EndDate,
  ]);

  // Tab 3: Compute book summary rows
  const tab3BookSummaryRows = useMemo(
    () => buildTab3BookSummaryRows(tab3ActivityBoardItems, books, activityTypes, permissionActivityTypeIds),
    [tab3ActivityBoardItems, books, activityTypes, permissionActivityTypeIds]
  );

  // Tab 3: Grand total
  const tab3GrandTotal = useMemo(() => calculateTab3GrandTotal(tab3BookSummaryRows), [tab3BookSummaryRows]);

  // Tab 3: Chart data (only books with totalHours > 0)
  const tab3ChartData = useMemo(() => buildTab3ChartData(tab3BookSummaryRows), [tab3BookSummaryRows]);

  // Tab 3: DataGrid columns for master rows
  const tab3Columns: GridColDef<Tab3BookSummaryRow>[] = useMemo(
    () => [
      {
        field: 'bookTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.projectName',
          defaultMessage: 'Proje Adı',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 3: DataGrid columns for detail (activity type) rows
  const tab3DetailColumns: GridColDef<Tab3ActivityTypeRow>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.activityType',
          defaultMessage: 'Aktivite Türü',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 3: Detail panel content (activity type breakdown per project)
  const getTab3DetailPanelContent = useCallback(
    (params: GridRowParams<Tab3BookSummaryRow>) => {
      const row = params.row;
      if (!row.activityTypeRows || row.activityTypeRows.length === 0) return null;

      return (
        <Box sx={{ p: 2 }}>
          <ZebraDataGrid<Tab3ActivityTypeRow>
            columns={tab3DetailColumns}
            rows={row.activityTypeRows}
            getRowId={(r) => r.activityTypeId}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [tab3DetailColumns, dataGridLocaleText]
  );

  // Tab 3: Detail panel height
  const getTab3DetailPanelHeight = useCallback((params: GridRowParams<Tab3BookSummaryRow>) => {
    const count = params.row.activityTypeRows?.length || 0;
    if (count === 0) return 0;
    return count * 36 + 56 + 16;
  }, []);

  // Tab 3: Pinned rows (grand total at bottom)
  const tab3PinnedRows = useMemo(
    () => ({
      bottom: [
        {
          bookId: -1,
          bookTitle: intl.formatMessage({
            id: 'activityMonitoring.grandTotal',
            defaultMessage: 'Genel Toplam',
          }),
          spentHours: tab3GrandTotal.spentHours,
          overtimeHours: tab3GrandTotal.overtimeHours,
          totalHours: tab3GrandTotal.totalHours,
          activityTypeRows: [],
        } as Tab3BookSummaryRow,
      ],
    }),
    [intl, tab3GrandTotal]
  );

  // useEffect hooks
  useEffect(() => {
    if (isActivityBoardItemUserScope && tabValue !== 0 && tabValue !== 2) {
      setTabValue(0);
    }
  }, [isActivityBoardItemUserScope, tabValue]);

  useEffect(() => {
    getActivityBoardItems();
  }, [getActivityBoardItems]);

  useEffect(() => {
    getTab2ActivityBoardItems();
  }, [getTab2ActivityBoardItems]);

  useEffect(() => {
    getTab3ActivityBoardItems();
  }, [getTab3ActivityBoardItems]);

  const supportingDataWarning = useMemo(() => {
    if (!arePermissionsInitialized) return null;

    if (!canReadPermissionActivities) {
      return intl.formatMessage({
        id: 'activityMonitoring.permissionAccessMissing',
        defaultMessage: 'İzin verisine erişim yetkisi olmadığı için izin ve atıl süre hesapları gösterilemiyor.',
      });
    }

    if (!isPermissionActivityScopeCompatible) {
      return intl.formatMessage({
        id: 'activityMonitoring.permissionScopeMismatch',
        defaultMessage:
          'Tüm kullanıcıların atıl süresini hesaplamak için yönetici kapsamındaki izin verisi gereklidir.',
      });
    }

    if (permissionActivityStatus === 'error' || holidayStatus === 'error') {
      return intl.formatMessage({
        id: 'activityMonitoring.permissionDataLoadError',
        defaultMessage:
          'İzin veya resmi tatil verisi yüklenemedi. Yanlış sonuç göstermemek için izin ve atıl süre hesapları gizlendi.',
      });
    }

    if (activityLoadError || tab2ActivityLoadError || tab3ActivityLoadError) {
      return intl.formatMessage({
        id: 'activityMonitoring.activityDataLoadError',
        defaultMessage: 'Aktivite verisi yüklenemedi. Yanlış sonuç göstermemek için etkilenen hesaplar gizlendi.',
      });
    }

    return null;
  }, [
    arePermissionsInitialized,
    activityLoadError,
    canReadPermissionActivities,
    holidayStatus,
    intl,
    isPermissionActivityScopeCompatible,
    permissionActivityStatus,
    tab2ActivityLoadError,
    tab3ActivityLoadError,
  ]);

  return (
    <>
      <Paper
        sx={{
          borderRadius: '8px',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
            },
          }}>
          <Tab
            value={0}
            id="activity-monitoring-tab-0"
            aria-controls="activity-monitoring-tabpanel-0"
            label={intl.formatMessage({
              id: 'activityMonitoring.tab1',
              defaultMessage: 'Kullanıcı Bazlı Özet',
            })}
          />
          {!isActivityBoardItemUserScope && (
            <Tab
              value={1}
              id="activity-monitoring-tab-1"
              aria-controls="activity-monitoring-tabpanel-1"
              label={intl.formatMessage({
                id: 'activityMonitoring.tab2',
                defaultMessage: 'Aktivite Bazlı Özet',
              })}
            />
          )}
          <Tab
            value={2}
            id="activity-monitoring-tab-2"
            aria-controls="activity-monitoring-tabpanel-2"
            label={intl.formatMessage({
              id: 'activityMonitoring.tab3',
              defaultMessage: 'Proje Bazlı Özet',
            })}
          />
        </Tabs>
      </Paper>

      {supportingDataWarning && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
          {supportingDataWarning}
        </Alert>
      )}

      {/* Tab 1 - User Summary */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Grid container justifyContent="flex-start" spacing={1} sx={{ mb: 2 }}>
          {!isActivityBoardItemUserScope && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={activityUsers}
                value={selectedUser}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: User | null) => setSelectedUser(newValue)}
                getOptionLabel={(option: User) => option.nameSurname ?? ''}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<FormattedMessage id="activityMonitoring.user" defaultMessage="Kullanıcı" />}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: {
                          fontSize: '1rem',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.startDate" defaultMessage="Başlangıç Tarihi" />}
              localeText={renderDatePickerLocaleText(intl)}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.endDate" defaultMessage="Bitiş Tarihi" />}
              localeText={renderDatePickerLocaleText(intl)}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" fontWeight={600} noWrap>
              <FormattedMessage id="activityMonitoring.expectedHours" defaultMessage="Seçilen Mesai Süresi" />
              :&nbsp;{displayedExpectedHours ?? '—'}
              &nbsp;
              <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
            </Typography>
          </Grid>
        </Grid>

        {/* DataGrid Table + Horizontal Bar Chart Side by Side */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: isActivityBoardItemUserScope ? 12 : 6 }}>
            <Box
              sx={{
                height: '70vh',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                borderRadius: '8px',
              }}>
              <ZebraDataGrid<UserSummaryRow>
                pagination
                columns={columns}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                loading={loading || isSupportingDataLoading}
                localeText={renderDataGridLocaleText(intl)}
                rows={userSummaryRows}
                getDetailPanelContent={getTab1DetailPanelContent}
                getDetailPanelHeight={getTab1DetailPanelHeight}
                initialState={{
                  aggregation: {
                    model: {
                      spentHours: 'sum',
                      totalHours: 'sum',
                    },
                  },
                }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
                }}
                slotProps={{
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                      columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                    },
                  },
                }}
                disableAggregation={false}
                disableRowGrouping
              />
            </Box>
          </Grid>

          {!isActivityBoardItemUserScope && (
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  height: '70vh',
                  overflow: 'auto',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    <FormattedMessage id="activityMonitoring.totalSpentHours" defaultMessage="Toplam Harcanan Süre" />
                    :&nbsp;{totalSpentHours}&nbsp;
                    <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
                  </Typography>
                </Box>

                {chartData.yLabels.length > 0 && (
                  <BarChart
                    layout="horizontal"
                    yAxis={[
                      {
                        scaleType: 'band',
                        data: chartData.yLabels,
                      },
                    ]}
                    series={[
                      {
                        data: chartData.values,
                        label: intl.formatMessage({
                          id: 'activityMonitoring.spentHours',
                          defaultMessage: 'Harcanan Süre (saat)',
                        }),
                        color: theme.palette.primary.main,
                      },
                    ]}
                    height={Math.max(300, chartData.yLabels.length * 40)}
                    margin={{ left: 150 }}
                  />
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab 2 - Activity Type Summary */}
      {!isActivityBoardItemUserScope && (
        <TabPanel value={tabValue} index={1}>
          {/* Filters */}
          <Grid container justifyContent="flex-start" spacing={1} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={activityUsers}
                value={tab2SelectedUser}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: User | null) => setTab2SelectedUser(newValue)}
                getOptionLabel={(option: User) => option.nameSurname ?? ''}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<FormattedMessage id="activityMonitoring.user" defaultMessage="Kullanıcı" />}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: {
                          fontSize: '1rem',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label={<FormattedMessage id="activityMonitoring.startDate" defaultMessage="Başlangıç Tarihi" />}
                localeText={renderDatePickerLocaleText(intl)}
                value={tab2StartDate}
                onChange={(newValue) => setTab2StartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label={<FormattedMessage id="activityMonitoring.endDate" defaultMessage="Bitiş Tarihi" />}
                localeText={renderDatePickerLocaleText(intl)}
                value={tab2EndDate}
                onChange={(newValue) => setTab2EndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6, md: 5 }}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <ActivityBreakdownSwitcher value={activitySummaryBreakdown} onChange={setActivitySummaryBreakdown} />
            </Grid>
          </Grid>

          {/* Table + Chart Side by Side */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box
                sx={{
                  height: '70vh',
                  border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                  borderRadius: '8px',
                }}>
                <ZebraDataGrid<ActivitySummaryRow>
                  pagination
                  columns={tab2Columns}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.activityTypeId}
                  loading={tab2Loading || isSupportingDataLoading}
                  localeText={dataGridLocaleText}
                  rows={activityTypeSummaryRows}
                  pinnedRows={tab2PinnedRows}
                  getDetailPanelContent={getTab2DetailPanelContent}
                  getDetailPanelHeight={getTab2DetailPanelHeight}
                  slots={{
                    noRowsOverlay: CustomNoRowsOverlay,
                    toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
                  }}
                  slotProps={{
                    filterPanel: {
                      filterFormProps: {
                        logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                        columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                        operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                        valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                      },
                    },
                  }}
                  getRowClassName={(params) => (params.row.activityTypeId === -1 ? 'row--total' : '')}
                  disableRowGrouping
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  height: '70vh',
                  overflow: 'auto',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    <FormattedMessage id="activityMonitoring.totalTime" defaultMessage="Toplam Süre" />
                    :&nbsp;{tab2GrandTotal.totalHours}&nbsp;
                    <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
                  </Typography>
                </Box>

                {tab2ChartData.yLabels.length > 0 && (
                  <BarChart
                    layout="horizontal"
                    yAxis={[
                      {
                        scaleType: 'band',
                        data: tab2ChartData.yLabels,
                      },
                    ]}
                    series={[
                      {
                        data: tab2ChartData.values,
                        label: intl.formatMessage({
                          id: 'activityMonitoring.totalTime',
                          defaultMessage: 'Toplam Süre',
                        }),
                        color: theme.palette.primary.main,
                      },
                    ]}
                    height={Math.max(300, tab2ChartData.yLabels.length * 40)}
                    margin={{ left: 150 }}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      )}

      {/* Tab 3 - Project Summary */}
      <TabPanel value={tabValue} index={2}>
        {/* Filters */}
        <Grid container justifyContent="flex-start" spacing={1} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              options={books}
              value={tab3SelectedBook}
              {...renderAutoCompleteLocaleText(intl)}
              onChange={(_, newValue: ShortBook | null) => setTab3SelectedBook(newValue)}
              getOptionLabel={(option: ShortBook) => option.title ?? ''}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={<FormattedMessage id="activityMonitoring.book" defaultMessage="Proje" />}
                  sx={{
                    '& .MuiButtonBase-root': {
                      border: 'none',
                      backgroundColor: 'inherit',
                      width: '2.25rem',
                      height: '2.25rem',
                      padding: '0.25rem',
                      [`& .${svgIconClasses.root}`]: {
                        fontSize: '1rem',
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                      },
                    },
                  }}
                />
              )}
            />
          </Grid>
          {!isActivityBoardItemUserScope && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={activityUsers}
                value={tab3SelectedUser}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: User | null) => setTab3SelectedUser(newValue)}
                getOptionLabel={(option: User) => option.nameSurname ?? ''}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<FormattedMessage id="activityMonitoring.users" defaultMessage="Kullanıcılar" />}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: {
                          fontSize: '1rem',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.startDate" defaultMessage="Başlangıç Tarihi" />}
              localeText={renderDatePickerLocaleText(intl)}
              value={tab3StartDate}
              onChange={(newValue) => setTab3StartDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.endDate" defaultMessage="Bitiş Tarihi" />}
              localeText={renderDatePickerLocaleText(intl)}
              value={tab3EndDate}
              onChange={(newValue) => setTab3EndDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Table + Chart Side by Side */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box
              sx={{
                height: '70vh',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                borderRadius: '8px',
              }}>
              <ZebraDataGrid<Tab3BookSummaryRow>
                pagination
                columns={tab3Columns}
                disableRowSelectionOnClick
                getRowId={(row) => row.bookId}
                loading={tab3Loading || isPermissionActivityDataLoading}
                localeText={dataGridLocaleText}
                rows={tab3BookSummaryRows}
                pinnedRows={tab3PinnedRows}
                getDetailPanelContent={getTab3DetailPanelContent}
                getDetailPanelHeight={getTab3DetailPanelHeight}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
                }}
                slotProps={{
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                      columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                    },
                  },
                }}
                getRowClassName={(params) => (params.row.bookId === -1 ? 'row--total' : '')}
                disableRowGrouping
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: '8px',
                height: '70vh',
                overflow: 'auto',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  <FormattedMessage id="activityMonitoring.totalTime" defaultMessage="Toplam Süre" />
                  :&nbsp;{tab3GrandTotal.totalHours}&nbsp;
                  <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
                </Typography>
              </Box>

              {tab3ChartData.yLabels.length > 0 && (
                <BarChart
                  layout="horizontal"
                  yAxis={[
                    {
                      scaleType: 'band',
                      data: tab3ChartData.yLabels,
                    },
                  ]}
                  series={[
                    {
                      data: tab3ChartData.values,
                      label: intl.formatMessage({
                        id: 'activityMonitoring.totalTime',
                        defaultMessage: 'Toplam Süre',
                      }),
                      color: theme.palette.primary.main,
                    },
                  ]}
                  height={Math.max(300, tab3ChartData.yLabels.length * 40)}
                  margin={{ left: 150 }}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>
    </>
  );
};

export default ActivityMonitoring;
